import { clampVolume, composeCue, composeMusic, voiceBudgetAllows, type Cue, type MusicScene, type Note, type Score } from "./score.js";
import { createMix, renderMusicBuffer, scheduleNote, type Mix, type Voice } from "./synth.js";
import { MusicLibrary } from "./music.js";

interface Loop { score: Score; start: number; cursor: number; channel: "sound" | "music" }
interface ActiveVoice { voice: Voice; cue: Cue | null; channel: "sound" | "music" }
interface Playing { source: AudioBufferSourceNode; gain: GainNode; scene: MusicScene; kind: "file" | "synth" }

/** The stingers a session reaches for first; they are decoded once the music plays, so their first play is the real piece. */
const WARM_CUES: readonly Cue[] = ["victory", "defeat", "pull.tension", "reveal.S", "reveal.A"];
/** How long one loop gives way to the next when the scene changes, and how long a fresh start fades in. */
const CROSSFADE_S = 0.8, FADE_IN_S = 0.35;
/** What a rendered stinger counts for in the voice budget (score.ts sums synth note gains). */
const STINGER_GAIN = 0.3;
/** The rendered files sit at -18 LUFS; this trim keeps loop and stinger at the level the synth's pieces were mixed against. */
const FILE_GAIN = 0.7;

/** One context for the application, created only by a trusted gesture in a visible authenticated view. */
export class GameAudio {
  private context: AudioContext | null = null;
  private mix: Mix | null = null;
  private active = false;
  private foreground = true;
  private pagePresent = true;
  private backgroundMusic = false;
  private unlocked = false;
  private scene: MusicScene = "base";
  private sound = 0;
  private music = 0;
  private voices = new Set<ActiveVoice>();
  private loops: Loop[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private throttle = new Map<string, number>();
  private generation = 0;
  private playing: Playing | null = null;
  private fading: Playing[] = [];
  private library: MusicLibrary | null = null;
  private warmed = false;
  private musicBuffers = new Map<MusicScene, Promise<AudioBuffer>>();
  private musicRequest = 0;

  mount(): () => void {
    const gesture = (event: Event) => { if (event.isTrusted) void this.unlock(); };
    const click = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest("button, a, [role=button]") : null;
      if (event.isTrusted && target && !target.matches(":disabled, [aria-disabled=true]")) this.play("ui.tap");
    };
    const visibility = () => this.setForeground(document.visibilityState === "visible" && document.hasFocus());
    const hide = () => this.setForeground(false);
    const leave = () => { this.pagePresent = false; this.pause(); };
    const enter = () => { this.pagePresent = true; visibility(); };
    document.addEventListener("pointerdown", gesture, true);
    document.addEventListener("keydown", gesture, true);
    document.addEventListener("click", click, true);
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("focus", visibility);
    window.addEventListener("blur", hide);
    window.addEventListener("pagehide", leave);
    window.addEventListener("pageshow", enter);
    visibility();
    return () => {
      document.removeEventListener("pointerdown", gesture, true);
      document.removeEventListener("keydown", gesture, true);
      document.removeEventListener("click", click, true);
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("focus", visibility);
      window.removeEventListener("blur", hide);
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("pageshow", enter);
      this.dispose();
    };
  }

  configure(settings: { sound: number; music: number; backgroundMusic?: boolean }): void {
    const wasSilent = (this.sound === 0 && settings.sound > 0) || (this.music === 0 && settings.music > 0);
    this.sound = clampVolume(settings.sound);
    this.music = clampVolume(settings.music);
    if (settings.backgroundMusic !== undefined) this.backgroundMusic = settings.backgroundMusic;
    if (this.context && this.mix) {
      this.mix.sound.gain.setTargetAtTime(this.sound, this.context.currentTime, 0.025);
      this.mix.music.gain.setTargetAtTime(this.music, this.context.currentTime, 0.08);
    }
    if (this.sound === 0) this.stopChannel("sound");
    if (this.music === 0) this.stopMusic();
    if (wasSilent && this.canPlay()) { this.stopLoops(); this.startLoops(); }
    if (!this.canListen()) this.pause();
    else if (this.unlocked) void this.resume();
  }

  setActive(active: boolean): void {
    if (active === this.active) return;
    this.active = active;
    if (!active) this.pause();
    else if (this.unlocked) void this.resume();
  }

  setScene(scene: MusicScene): void {
    if (scene === this.scene) return;
    this.scene = scene;
    this.stopLoops();
    // The loop that is playing keeps playing until the next scene's loop is decoded; then they cross (D97).
    if (!this.playing) this.stopMusic();
    if (this.unlocked) void this.resume();
  }

  private setForeground(foreground: boolean): void {
    this.foreground = foreground;
    if (!foreground) { this.stopLoops(); this.stopChannel("sound"); }
    if (!this.canListen()) this.pause();
    else if (this.unlocked) void this.resume();
  }

  private async unlock(): Promise<void> {
    if (!this.active || !this.foreground || !this.pagePresent || typeof AudioContext === "undefined") return;
    this.unlocked = true;
    if (!this.context) {
      this.context = new AudioContext({ latencyHint: "interactive" });
      this.mix = createMix(this.context, this.sound, this.music);
      this.library = new MusicLibrary(this.context);
    }
    await this.resume();
  }

  private async resume(): Promise<void> {
    const context = this.context;
    if (!context || !this.canListen()) return;
    const generation = this.generation;
    try {
      await context.resume();
      if (generation !== this.generation || !this.canListen()) { if (!this.canListen() && context.state !== "closed") await context.suspend(); return; }
      if (this.foreground && !this.timer) this.startLoops();
      void this.startMusic();
    } catch { /* Autoplay policy or an unavailable audio device leaves the game silent. */ }
  }

  private canPlay(): boolean { return this.active && this.foreground && this.unlocked && this.context?.state === "running"; }
  private canListen(): boolean { return this.active && this.pagePresent && (this.foreground || (this.backgroundMusic && this.music > 0)); }

  /**
   * The scene's loop: the rendered piece from /assets/music when the library has it, the browser
   * synth's loop otherwise (D97). A change of scene crosses from the old loop into the new one.
   */
  private async startMusic(): Promise<void> {
    const context = this.context, scene = this.scene, request = ++this.musicRequest;
    if (!context || !this.mix || this.music === 0 || !this.canListen() || this.playing?.scene === scene) return;
    let buffer: AudioBuffer | null = null, kind: Playing["kind"] = "synth";
    try { buffer = this.library ? await this.library.loop(scene) : null; if (buffer) kind = "file"; } catch { buffer = null; }
    if (!buffer) {
      let pending = this.musicBuffers.get(scene);
      if (!pending) { pending = renderMusicBuffer(composeMusic(scene)); this.musicBuffers.set(scene, pending); }
      try { buffer = await pending; } catch { this.musicBuffers.delete(scene); return; }
    }
    if (request !== this.musicRequest || context !== this.context || scene !== this.scene || !this.mix || !this.canListen() || this.music === 0 || context.state !== "running") return;
    const source = context.createBufferSource(), gain = context.createGain(), now = context.currentTime;
    source.buffer = buffer; source.loop = true; source.connect(gain); gain.connect(this.mix.music);
    const fade = this.playing ? CROSSFADE_S : FADE_IN_S;
    gain.gain.setValueAtTime(0, now); gain.gain.linearRampToValueAtTime(kind === "file" ? FILE_GAIN : 1, now + fade);
    source.start();
    if (this.playing) this.fadeOut(this.playing, fade);
    this.playing = { source, gain, scene, kind };
    if (kind === "file" && !this.warmed && this.library) { this.warmed = true; void this.library.warm(WARM_CUES); }
  }

  private fadeOut(playing: Playing, seconds: number): void {
    const context = this.context;
    if (!context) return;
    const now = context.currentTime;
    playing.gain.gain.cancelScheduledValues(now);
    playing.gain.gain.setValueAtTime(playing.gain.gain.value, now);
    playing.gain.gain.linearRampToValueAtTime(0, now + seconds);
    playing.source.stop(now + seconds + 0.05);
    this.fading.push(playing);
    playing.source.onended = () => { playing.source.disconnect(); playing.gain.disconnect(); this.fading = this.fading.filter((entry) => entry !== playing); };
  }

  private stopMusic(): void {
    ++this.musicRequest;
    for (const entry of [this.playing, ...this.fading]) if (entry) { entry.source.onended = null; entry.source.stop(); entry.source.disconnect(); entry.gain.disconnect(); }
    this.playing = null; this.fading = [];
  }

  play(cue: Cue): void {
    if (!this.canPlay() || !this.context || this.sound === 0) return;
    const now = this.context.currentTime;
    const key = cue.startsWith("hit.") ? "hit" : cue;
    const gap = key === "hit" ? 0.09 : cue === "ui.tap" ? 0.045 : cue === "reward" || cue === "build.complete" ? 0.7 : 0.18;
    if (now - (this.throttle.get(key) ?? -Infinity) < gap) return;
    this.throttle.set(key, now);
    // A rendered stinger plays when it is decoded already; otherwise the synth's cue plays now and the
    // piece is decoded for the next time — a click never waits for the network (D97).
    const rendered = this.library?.peekCue(cue) ?? null;
    if (rendered) { this.buffer(rendered, cue); return; }
    if (this.library) void this.library.cue(cue).catch(() => null);
    for (const note of composeCue(cue).notes) this.note(note, now + note.at, "sound", cue);
  }

  private buffer(buffer: AudioBuffer, cue: Cue): void {
    if (!this.context || !this.mix || !voiceBudgetAllows([...this.voices].map(({ voice }) => voice.gain), STINGER_GAIN)) return;
    const context = this.context, source = context.createBufferSource(), gain = context.createGain();
    source.buffer = buffer; gain.gain.value = FILE_GAIN; source.connect(gain); gain.connect(this.mix.sound);
    let done = false;
    const disconnect = () => { if (done) return; done = true; source.disconnect(); gain.disconnect(); this.voices.delete(entry); };
    const entry: ActiveVoice = { cue, channel: "sound", voice: { gain: STINGER_GAIN, disconnect, stop: () => { const now = context.currentTime; gain.gain.cancelScheduledValues(now); gain.gain.setValueAtTime(gain.gain.value, now); gain.gain.linearRampToValueAtTime(0, now + 0.06); source.stop(now + 0.08); } } };
    source.onended = disconnect;
    source.start();
    this.voices.add(entry);
  }

  stop(cue: Cue): void { for (const active of this.voices) if (active.cue === cue) active.voice.stop(); }

  private note(note: Note, at: number, channel: "sound" | "music", cue: Cue | null): void {
    if (!this.context || !this.mix || !voiceBudgetAllows([...this.voices].map(({ voice }) => voice.gain), note.gain)) return;
    const entry: ActiveVoice = { voice: scheduleNote(this.context, this.mix[channel], note, at, () => this.voices.delete(entry)), cue, channel };
    this.voices.add(entry);
  }

  private startLoops(): void {
    if (!this.context || this.timer || !this.foreground || this.sound === 0 || this.scene === "battle") return;
    const start = this.context.currentTime + 0.06;
    this.loops = [{ score: composeCue(this.scene === "base" ? "base.ambience" : "map.wind"), start, cursor: 0, channel: "sound" }];
    const tick = () => {
      if (!this.canPlay() || !this.context) return;
      const now = this.context.currentTime;
      for (const loop of this.loops) {
        if (now > loop.start + loop.score.duration + 0.5) { loop.start = now + 0.04; loop.cursor = 0; }
        while (loop.cursor < loop.score.notes.length) {
          const note = loop.score.notes[loop.cursor]!;
          const at = loop.start + note.at;
          if (at > now + 0.18) break;
          if (at >= now - 0.06 && this[loop.channel] > 0) this.note(note, Math.max(now, at), loop.channel, null);
          loop.cursor++;
        }
        if (loop.cursor >= loop.score.notes.length && now + 0.18 >= loop.start + loop.score.duration) { loop.start += loop.score.duration; loop.cursor = 0; }
      }
    };
    tick();
    this.timer = setInterval(tick, 90);
  }

  private stopChannel(channel: "sound" | "music"): void { for (const active of this.voices) if (active.channel === channel) active.voice.stop(); }
  private stopLoops(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.loops = [];
    for (const active of this.voices) if (active.cue === null) active.voice.stop();
  }

  private pause(): void {
    ++this.generation;
    this.stopLoops();
    this.stopMusic();
    for (const active of this.voices) { active.voice.stop(); active.voice.disconnect(); }
    this.voices.clear();
    if (this.context?.state === "running") void this.context.suspend().catch(() => {});
  }

  inspect() { return { context: this.context?.state ?? null, active: this.active, foreground: this.foreground, backgroundMusic: this.backgroundMusic, scene: this.scene, sound: this.sound, music: this.music, voices: this.voices.size + (this.playing ? 1 : 0), scheduling: this.timer !== null || this.playing !== null, musicLooping: this.playing?.source.loop ?? false, musicKind: this.playing?.kind ?? null, musicScene: this.playing?.scene ?? null, fading: this.fading.length, musicClock: this.context?.currentTime ?? 0 }; }

  /** What the library offers and holds decoded, for the browser proof. */
  describeMusic(): Promise<{ loops: string[]; cues: string[]; ready: string[] } | null> { return this.library ? this.library.describe() : Promise.resolve(null); }

  dispose(): void {
    this.pause();
    this.mix?.dispose(); this.mix = null;
    if (this.context && this.context.state !== "closed") void this.context.close().catch(() => {});
    this.context = null; this.unlocked = false; this.throttle.clear();
    this.musicBuffers.clear(); this.library = null; this.warmed = false;
  }
}

export const audio = new GameAudio();
